import type { TFunction } from "i18next";
import * as XLSX from "xlsx";
import { z } from "zod";
import {
  EXCEL_FILE_ACCEPTED_EXTENSIONS,
  EXCEL_FILE_ACCEPTED_MIME_TYPES,
  EXCEL_FILE_MAX_SIZE,
  EXCEL_REQUIRED_COLUMNS,
  EXCEL_REQUIRED_TOTAL_ROWS,
} from "@/constants/validation";

export const PROFILE_METHODS = ["PREDEFINED_PROFILE", "TABLE_INPUT", "FILE_INPUT"] as const;
export const PROFILE_TYPES = ["ONE_SHIFT", "TWO_SHIFTS", "THREE_SHIFTS"] as const;
export const CONSUMPTION_TYPES = ["MONTHLY", "YEARLY"] as const;

export type EnergyProfileMethod = (typeof PROFILE_METHODS)[number];
export type ProfileType = (typeof PROFILE_TYPES)[number];
export type ConsumptionType = (typeof CONSUMPTION_TYPES)[number];

export const getStep1Schema = (t: TFunction) => {
  return z
    .object({
      energyProfileMethod: z.enum(PROFILE_METHODS),
      predefinedProfile: z.enum(PROFILE_TYPES).optional(),
      consumptionType: z.enum(CONSUMPTION_TYPES).optional(),
      consumption: z.string().optional(),
      tableData: z.array(z.string().optional()).optional(),
      excelProfile: z.any().optional(),
    })

    .superRefine((data, ctx) => {
      if (data.energyProfileMethod === "PREDEFINED_PROFILE") {
        if (!data.predefinedProfile) {
          ctx.addIssue({
            code: "custom",
            path: ["predefinedProfile"],
            message: t("step1.validation.predefinedProfile"),
          });
        }

        if (!data.consumptionType) {
          ctx.addIssue({
            code: "custom",
            path: ["consumptionType"],
            message: t("step1.validation.consumptionType"),
          });
        }

        if (!data.consumption || data.consumption.trim() === "") {
          ctx.addIssue({
            code: "custom",
            path: ["consumption"],
            message: t("step1.validation.consumptionRequired"),
          });
        }
      }

      if (data.energyProfileMethod === "TABLE_INPUT") {
        const arr = Array.isArray(data.tableData) ? data.tableData : [];

        arr.forEach((val, i) => {
          if (!val || val.trim() === "") {
            ctx.addIssue({
              code: "custom",
              path: ["tableData", i],
              message: t("step1.validation.tableValueRequired"),
            });
          }
        });
      }

      if (data.energyProfileMethod === "FILE_INPUT") {
        const excel = data.excelProfile as unknown;

        if (typeof excel === "string" && excel.trim() !== "") {
          return;
        }

        const file = excel as File | undefined;

        if (!(file instanceof File) || file.size === 0) {
          ctx.addIssue({
            code: "custom",
            path: ["excelProfile"],
            message: t("step1.validation.fileRequired"),
          });
          return;
        }

        if (!EXCEL_FILE_ACCEPTED_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext))) {
          ctx.addIssue({
            code: "custom",
            path: ["excelProfile"],
            message: t("step1.validation.invalidExtension", {
              allowed: EXCEL_FILE_ACCEPTED_EXTENSIONS.join(", "),
            }),
          });
        }

        if (!EXCEL_FILE_ACCEPTED_MIME_TYPES.includes(file.type)) {
          ctx.addIssue({
            code: "custom",
            path: ["excelProfile"],
            message: t("step1.validation.invalidType", {
              allowed: EXCEL_FILE_ACCEPTED_MIME_TYPES.join(", "),
            }),
          });
        }

        if (file.size > EXCEL_FILE_MAX_SIZE) {
          ctx.addIssue({
            code: "custom",
            path: ["excelProfile"],
            message: t("step1.validation.fileTooLarge"),
          });
        }
      }
    })
    .superRefine(async (data, ctx) => {
      if (data.energyProfileMethod !== "FILE_INPUT") return;

      const excel = data.excelProfile as unknown;
      if (!(excel instanceof File) || excel.size === 0) return;

      const hasFormatError =
        !EXCEL_FILE_ACCEPTED_EXTENSIONS.some(ext => excel.name.toLowerCase().endsWith(ext)) ||
        !EXCEL_FILE_ACCEPTED_MIME_TYPES.includes(excel.type);
      if (hasFormatError) return;

      try {
        const buffer = await excel.arrayBuffer();
        const workbook = XLSX.read(buffer);
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        const allRows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1 });
        const nonEmptyRows = allRows.filter(row =>
          row.some(cell => cell !== null && cell !== undefined && cell !== ""),
        );
        const numRows = nonEmptyRows.length;
        const numCols = nonEmptyRows.length > 0 ? Math.max(...nonEmptyRows.map(r => r.length)) : 0;

        if (numCols !== EXCEL_REQUIRED_COLUMNS) {
          ctx.addIssue({
            code: "custom",
            path: ["excelProfile"],
            message: t("step1.validation.invalidColumnCount", {
              required: EXCEL_REQUIRED_COLUMNS,
            }),
          });
        }

        if (numCols === EXCEL_REQUIRED_COLUMNS && numRows > 0 && nonEmptyRows[0][1] !== "P_demand") {
          ctx.addIssue({
            code: "custom",
            path: ["excelProfile"],
            message: t("step1.validation.invalidSecondColumnName"),
          });
        }

        if (numRows !== EXCEL_REQUIRED_TOTAL_ROWS) {
          ctx.addIssue({
            code: "custom",
            path: ["excelProfile"],
            message: t("step1.validation.invalidRowCount", {
              required: EXCEL_REQUIRED_TOTAL_ROWS,
            }),
          });
        }

        if (numRows === EXCEL_REQUIRED_TOTAL_ROWS && numCols === EXCEL_REQUIRED_COLUMNS) {
          const datetimePattern = /^\d{1,2}\.\d{1,2}\.\d{4} {2}\d{2}:\d{2}:\d{2}$/;
          const dataRows = nonEmptyRows.slice(1);
          const hasInvalidDate = dataRows.some(row => {
            const cell = row[0];
            if (typeof cell === "number" && cell > 0) return false;
            if (cell instanceof Date) return false;
            if (typeof cell === "string") return !datetimePattern.test(cell);
            return true;
          });
          if (hasInvalidDate) {
            ctx.addIssue({
              code: "custom",
              path: ["excelProfile"],
              message: t("step1.validation.invalidDateFormat"),
            });
          }
        }
      } catch {
        ctx.addIssue({
          code: "custom",
          path: ["excelProfile"],
          message: t("step1.validation.fileRequired"),
        });
      }
    });
};

export type Step1Schema = z.infer<ReturnType<typeof getStep1Schema>>;

export type Step1Keys = keyof Step1Schema;
